import { z } from 'zod';
import { deflateSync, strToU8 } from 'fflate';
import { toJS } from 'mobx';
import type Context from '@/context';
import type { IChipModel, ICircuitModel } from '@/interfaces/models';
import type { ChipModelDto, ProjectDto } from '@/schemas/chip';
import { DEFAULT_MI_SIDE_CONFIG, SIInputMode } from '@/models/ChipModel';
import { CircuitType, Side } from '@/schemas/common';
import {
  SimulationCheckpointSchema,
  type SimulationCheckpoint,
} from '@/models/logic/SimulationCheckpoint';
import { validateContinuationValue } from '@/services/projectSnapshot/ContinuationValue';
import { authorities } from './authority';
import { Signal } from '@/models/data/Signal';

export interface NativeContinuationMetadata {
  version: 1;
  authoredFingerprint: string;
  simulation: SimulationCheckpoint;
  sourceLevels: { index: number; value: boolean }[];
  inputWatermark: number;
}

/** Only an authoritative capture scope may expose a worker continuation here. */
export const captureContinuationMetadata = new WeakMap<
  object,
  NativeContinuationMetadata
>();
/** Validated hydrated owners; the session may suspend and transfer these directly. */
export const restoredContinuationMetadata = new WeakMap<
  object,
  NativeContinuationMetadata
>();
// Router navigation may remove the experimental query before project loading.
const initialCheckpointOptIn =
  typeof location !== 'undefined' &&
  new URLSearchParams(location.search).has('nativeRuntimeCheckpoint');

const metadataSchema = z
  .object({
    version: z.literal(1),
    authoredFingerprint: z.string().max(4_000_000),
    simulation: SimulationCheckpointSchema,
    sourceLevels: z
      .array(
        z.object({ index: z.number().int().nonnegative(), value: z.boolean() }).strict(),
      )
      .max(100_000),
    inputWatermark: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  })
  .strict();

/** Unknown versions are a load-time native fallback, not an authoring-file error. */
export function readContinuationMetadata(value: unknown): NativeContinuationMetadata {
  validateContinuationValue(value);
  const metadata = metadataSchema.parse(value);
  const sources = new Set<number>();
  for (const source of metadata.sourceLevels) {
    const node = metadata.simulation.nodes[source.index];
    if (!node || !['LEVER', 'BUTTON'].includes(node.type) || sources.has(source.index))
      throw Error('Invalid continuation source binding');
    if (node.type === 'BUTTON' && source.value)
      throw Error('Active Button requires native file-release fallback');
    sources.add(source.index);
  }
  if (
    sources.size !==
    metadata.simulation.nodes.filter((n) => ['LEVER', 'BUTTON'].includes(n.type)).length
  )
    throw Error('Incomplete continuation source intent');
  if (
    metadata.simulation.nodes.some(
      (node) =>
        node.type === 'BUTTON' && (node.output || (node.state as any)?.isOn === true),
    )
  )
    throw Error('Active Button requires native file-release fallback');
  return metadata;
}

function canonical(value: any): any {
  if (Array.isArray(value)) return value.map(canonical);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .filter((k) => value[k] !== undefined)
      .map((k) => [k, canonical(value[k])]),
  );
}

function tileConfiguration(type: string, state: any) {
  if (type === 'Clock') return { intervalMs: state?.intervalMs };
  if (type === 'Delay') return { delayTimeMs: state?.delayTimeMs };
  if (['SingleInterface', 'MultiInterface', 'BundleInterface'].includes(type))
    return (
      state &&
      Object.fromEntries(Object.entries(state).filter(([key]) => key !== 'toggleState'))
    );
  return undefined;
}

type AuthoredDefinition = Pick<
  ChipModelDto,
  | 'id'
  | 'kind'
  | 'adapterSignals'
  | 'logicCircuitDto'
  | 'structureCircuitDto'
  | 'structureOriginLocationDto'
  | 'interfaceLinks'
> & {
  chipInstanceDtos: Pick<
    ChipModelDto['chipInstanceDtos'][number],
    'id' | 'chipId' | 'originPointDto' | 'rotation' | 'bundleWidth'
  >[];
};

function definition(dto: AuthoredDefinition) {
  const circuit = (value: ChipModelDto['logicCircuitDto']) => ({
    tiles: value.tileManagerDto.tiles
      .map((tile) => ({
        id: tile.id,
        type: tile.tileType,
        position: tile.globalTileLocation,
        rotation: tile.meta.rotation,
        cable: tile.tileType === 'Wire' ? undefined : tile.meta.cable,
        bundle: tile.meta.bundle,
        partOfChip: tile.meta.partOfChip,
        configuration: tileConfiguration(tile.tileType, tile.state),
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
    connections: normalizedConnections(value.connectionGraphDto?.connections),
    disconnected: normalizedConnections(value.connectionGraphDto?.disconnected),
  });
  return {
    id: dto.id,
    kind: dto.kind,
    adapterSignals: dto.adapterSignals ?? {},
    logic: circuit(dto.logicCircuitDto),
    structure: circuit(dto.structureCircuitDto),
    origin: dto.structureOriginLocationDto ?? { x: 0, y: 0 },
    links: dto.interfaceLinks
      .map((link) => ({
        id: link.id,
        type: link.type,
        label: link.label,
        selectorSignalIds: link.selectorSignalIds,
        inputMode: link.inputMode,
        logicTileId: link.logicTileId,
        structureTileId: link.structureTileId,
        sideLabels: link.sideLabels,
        sideConfig: link.sideConfig,
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
    instances: dto.chipInstanceDtos
      .map((instance) => ({
        id: instance.id,
        chipId: instance.chipId,
        origin: instance.originPointDto,
        rotation: instance.rotation ?? 0,
        bundleWidth: instance.bundleWidth,
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  };
}

function normalizedConnections(
  connections:
    | { a: { tileId: string; side: string }; b: { tileId: string; side: string } }[]
    | undefined,
) {
  return (connections || [])
    .map((connection) =>
      [connection.a, connection.b].map((point) => `${point.tileId}:${point.side}`).sort(),
    )
    .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
}

function fingerprint(
  root: string,
  definitions: Map<string, ReturnType<typeof definition>>,
) {
  const seen = new Set<string>(),
    pending = [root];
  for (let i = 0; i < pending.length; i++) {
    const id = pending[i];
    if (seen.has(id)) continue;
    const current = definitions.get(id);
    if (!current) throw Error('Missing authored continuation dependency');
    seen.add(id);
    for (const instance of current.instances) pending.push(instance.chipId);
    if (seen.size > 10_000) throw Error('Continuation dependency budget exceeded');
  }
  const result = JSON.stringify(
    canonical({
      version: 1,
      root,
      definitions: [...seen].sort().map((id) => definitions.get(id)),
    }),
  );
  // Lossless comparison only: imported fingerprints are never inflated.
  if (result.length > 16 * 1024 * 1024)
    throw Error('Continuation authored descriptor exceeds limit');
  const bytes = strToU8(result);
  if (bytes.length > 16 * 1024 * 1024)
    throw Error('Continuation authored descriptor exceeds limit');
  const compressed = deflateSync(bytes, { level: 1 });
  let binary = '';
  for (let offset = 0; offset < compressed.length; offset += 8192)
    binary += String.fromCharCode(...compressed.subarray(offset, offset + 8192));
  const encoded = `deflate1:${btoa(binary)}`;
  if (encoded.length > 4_000_000)
    throw Error('Continuation authored fingerprint exceeds limit');
  return encoded;
}

export function authoredFingerprintFromProject(
  project: ProjectDto,
  chip: ChipModelDto,
): string {
  return fingerprint(
    chip.id,
    new Map(project.chips.map((current) => [current.id, definition(current)])),
  );
}

/** Match native DTO fields without materializing signals, runtime IDs or artwork. */
function liveDefinition(chip: IChipModel): ReturnType<typeof definition> {
  const circuit = (value: ICircuitModel): ReturnType<typeof definition>['logic'] => {
    const connections = value.connectionModel.dto;
    return {
      tiles: value.tiles
        .map((tile) => ({
          id: tile.id,
          type: tile.tileType,
          // TileInstance.dto explicitly reads these fields; its position may use
          // non-enumerable Point getters and cannot be copied by enumeration.
          position: {
            chipInstanceId: tile.globalTilePosition.chipInstanceId,
            circuitType: tile.globalTilePosition.circuitType,
            location: {
              x: tile.globalTilePosition.location.x,
              y: tile.globalTilePosition.location.y,
            },
          },
          rotation: tile.meta.rotation,
          cable: tile.tileType === 'Wire' ? undefined : tile.meta.cable,
          bundle: toJS(tile.meta.bundle),
          partOfChip: tile.meta.partOfChip,
          // Only interfaces contribute their complete detached configuration.
          // Ordinary gates/source states and Clock/Delay runtime fields are omitted.
          configuration: tileConfiguration(
            tile.tileType,
            ['SingleInterface', 'MultiInterface', 'BundleInterface'].includes(
              tile.tileType,
            )
              ? toJS(tile.state)
              : tile.state,
          ),
        }))
        .sort((a, b) => a.id.localeCompare(b.id)),
      connections: normalizedConnections(connections?.connections),
      disconnected: normalizedConnections(connections?.disconnected),
    };
  };
  return {
    id: chip.id,
    kind: chip.kind,
    adapterSignals: chip.adapterSignals ?? {},
    logic: circuit(chip.logicCircuit.circuitModel),
    structure: circuit(chip.structureCircuit.circuitModel),
    origin: chip.structureCircuit.originLocation.data,
    links: chip.interfaceLinks
      .map((link) => {
        // InterfaceLink.dto omits default mode/config and empty side labels.
        // Reproduce those omissions without serializing display artwork/maps.
        const sideConfig = Object.fromEntries(
          [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT]
            .filter(
              (side) =>
                link.sideConfig[side].active !== DEFAULT_MI_SIDE_CONFIG.active ||
                link.sideConfig[side].forceConnection !==
                  DEFAULT_MI_SIDE_CONFIG.forceConnection,
            )
            .map((side) => [
              side,
              {
                active: link.sideConfig[side].active,
                forceConnection: link.sideConfig[side].forceConnection,
              },
            ]),
        );
        return {
          id: link.id,
          type: link.type,
          label: link.label,
          selectorSignalIds: link.selectorSignalIds,
          inputMode: link.inputMode === SIInputMode.NONE ? undefined : link.inputMode,
          logicTileId: link.tiles[CircuitType.ChipLogic]?.id,
          structureTileId: link.tiles[CircuitType.ChipStructure]?.id,
          sideLabels: Object.keys(link.sideLabels).length
            ? { ...link.sideLabels }
            : undefined,
          sideConfig: Object.keys(sideConfig).length ? sideConfig : undefined,
        };
      })
      .sort((a, b) => a.id.localeCompare(b.id)),
    instances: [...chip.logicCircuit.chipInstances.values()]
      .map((instance) => ({
        id: instance.id,
        chipId: instance.chipId,
        origin: instance.originPoint.data,
        rotation: instance.rotation ?? 0,
        bundleWidth: instance.bundleWidth,
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  };
}

/** Authoring inputs only. Never read chip/graph DTOs while a worker owns runtime. */
export function authoredFingerprint(context: Context, chip: IChipModel): string {
  const definitions = new Map<string, ReturnType<typeof definition>>();
  const byId = new Map<string, IChipModel>(
    context.chipStore.chips.map((current) => [current.id, current]),
  );
  const pending: string[] = [chip.id];
  for (let i = 0; i < pending.length; i++) {
    const id = pending[i];
    if (definitions.has(id)) continue;
    const current = byId.get(id);
    if (!current) throw Error('Missing authored continuation dependency');
    const projected = liveDefinition(current);
    definitions.set(id, projected);
    for (const instance of projected.instances) pending.push(instance.chipId);
    if (definitions.size > 10_000) throw Error('Continuation dependency budget exceeded');
  }
  return fingerprint(chip.id, definitions);
}

export function nativeCheckpointOptIn() {
  return (
    initialCheckpointOptIn ||
    (typeof location !== 'undefined' &&
      new URLSearchParams(location.search).has('nativeRuntimeCheckpoint'))
  );
}

/** Native owner capture is passive; worker owners must supply their own exact cut. */
export function continuationForCapture(
  context: Context | undefined,
  chip: IChipModel,
): NativeContinuationMetadata | undefined {
  const supplied = captureContinuationMetadata.get(chip.logicCircuit);
  if (
    !supplied &&
    (!context?.nativeRuntimeCheckpoint ||
      context.mainChip !== chip ||
      authorities.has(chip.logicCircuit))
  )
    return undefined;
  try {
    if (supplied) return readContinuationMetadata(supplied);
    const simulation = chip.logicCircuit.logicSimulation.captureContinuation();
    const sources = new Map<number, boolean>(
      simulation.nodes.flatMap((node, index) =>
        ['LEVER', 'BUTTON'].includes(node.type)
          ? [[index, Boolean((node.state as any).isOn)] as const]
          : [],
      ),
    );
    const indexes = new Map(
      [...chip.logicCircuit.logicGraph.nodes.keys()].map((id, index) => [id, index]),
    );
    for (const tile of chip.logicCircuit.circuitModel.tiles) {
      if (!tile.state || typeof tile.state !== 'object' || !('toggleState' in tile.state))
        continue;
      for (const id of Object.values(tile.logicNodeIds || {})) {
        const index = indexes.get(id!);
        if (index !== undefined && sources.has(index))
          sources.set(index, tile.state.toggleState === Signal.HIGH);
      }
    }
    return readContinuationMetadata({
      version: 1,
      authoredFingerprint: authoredFingerprint(context!, chip),
      simulation,
      sourceLevels: [...sources].map(([index, value]) => ({ index, value })),
      inputWatermark: 0,
    });
  } catch (error) {
    if (context) {
      const status = `Native continuation unavailable: ${String(error)}`;
      context.nativeContinuationStatus = status;
      // AppModel publishes an observable context copy; chips retain the
      // original construction context. Report later capture fallback in both.
      const published = context.app.context;
      if (published?.mainChip === context.mainChip)
        published.nativeContinuationStatus = status;
    }
    return undefined;
  }
}

/** Restore authored intent independently of the saved, possibly dirty node state. */
export function restoreContinuationSources(
  chip: IChipModel,
  metadata: NativeContinuationMetadata,
) {
  const levels = new Map(
    metadata.sourceLevels.map((source) => [
      metadata.simulation.nodes[source.index].id,
      source.value,
    ]),
  );
  const graph = chip.logicCircuit.logicGraph;
  for (const tile of chip.logicCircuit.circuitModel.tiles) {
    if (!tile.state || typeof tile.state !== 'object' || !('toggleState' in tile.state))
      continue;
    const source = Object.values(tile.logicNodeIds || {})
      .map((id) => graph.nodes.get(id!))
      .find((node) => node && levels.has(node.idText));
    if (!source) continue;
    const level = levels.get(source.idText) ? Signal.HIGH : Signal.LOW;
    tile.state.toggleState = level;
    const linkId = (tile.state as any).connectedInterface?.interfaceLinkId;
    const link = linkId && chip.interfaceLinkMap.get(linkId);
    if (link)
      for (const paired of Object.values(link.tiles) as ({ state: any } | undefined)[])
        if (paired) (paired.state as any).toggleState = level;
  }
}
